import React, { useEffect, useState } from "react";
import {
	Modal,
	Button,
	Row,
	Col,
	Container,
	Form,
	Spinner,
} from "react-bootstrap";
import { Formik, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import {
	createRoleAsync,
	getAllPermissionsAsync,
	getAllRolesAsync,
} from "../../../../slices/settings/settingsSlice";
import { RootState } from "../../../../store/store";
import { toast } from "react-toastify";

interface CreateRoleValues {
	role_name: string;
	company_id: string;
	permissions: string[];
}

interface CreateRoleProps {
	closeModal: () => void;
	show: boolean;
	onCreateSuccess: () => void;
}

const CreateRole: React.FC<CreateRoleProps> = ({
	closeModal,
	show,
	onCreateSuccess,
}) => {
	const dispatch = useAppDispatch();
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [pageNumber, setPage] = useState(1);

	const companyId = useAppSelector(
		(state: RootState) => state.auth.user?.company_id || "",
	);
	const permissions =
		useAppSelector((state: RootState) => state.settings.permissions) || [];

	useEffect(() => {
		dispatch(getAllPermissionsAsync());
	}, [dispatch]);

	const getPermissionsByCategory = (category: string) => {
		return permissions.filter((permission) =>
			permission.permission_name.includes(category),
		);
	};
	console.log("Permissions:", permissions);
	return (
		<Container fluid className="">
			<Modal
				centered
				size="lg"
				show={show}
				onHide={closeModal}
				style={{ maxHeight: "100vh", overflowY: "auto" }}
			>
				<div className="px-5">
					<Formik
						initialValues={{
							role_name: "",
							company_id: companyId || "",
							permissions: [],
						}}
						validationSchema={Yup.object().shape({
							role_name: Yup.string().required("Required"),
							company_id: Yup.string().required("Required"),
							permissions: Yup.array().of(Yup.string()).required("Required"),
						})}
						onSubmit={(values: CreateRoleValues, { setSubmitting }) => {
							console.log("Submitting values:", values);
							dispatch(
								createRoleAsync({
									...values,
									company_id: companyId || "",
									permissions: values.permissions.map(String),
								}),
							)
								.unwrap()
								.then((response) => {
									toast.success("Role created successfully");
									dispatch(getAllRolesAsync({ companyId, page: pageNumber }));
									onCreateSuccess();
									closeModal();
								})
								.catch((error) => {
									const errorMessage = Object.values(error.message)
										.flat()
										.join(" ");
									toast.error(errorMessage);
								})
								.finally(() => {
									setSubmitting(false);
								});
						}}
					>
						{({
							errors,
							touched,
							handleSubmit,
							isSubmitting,
							values,
							handleChange,
						}) => (
							<Form onSubmit={handleSubmit}>
								<Modal.Header closeButton className="text-start mt-2">
									<h4 className="confirm-header">Create a role</h4>
								</Modal.Header>
								<Modal.Body>
									<Row>
										<Col md={6} className="mt-2">
											<label className="mt-2">
												<p>
													Role <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="text"
												name="role_name"
												placeholder="Enter role name"
												className={`fluent-input ${touched.role_name && errors.role_name
													? "is-invalid"
													: ""
													}`}
												required
											/>
										</Col>
									</Row>
									<Row>
										<Col md={12} className="mt-3">
											<p className="mt-2 fw-bold">Permissions</p>
											<FieldArray
												name="permissions"
												render={(arrayHelpers) => (
													<div>
														{/* Request Permissions */}
														<Row>
															<Col md={2} className="mt-2">
																<p className="fw-bold">Request</p>
															</Col>
															<Col className="mt-1 text-center">
																<span>Create</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value={20}
																	onChange={(e) => {
																		if (e.target.checked) {
																			arrayHelpers.push(String(20));
																		} else {
																			arrayHelpers.remove(20);
																		}
																	}}
																/>
															</Col>
															<Col className="mt-1 text-center">
																<span>Edit</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value={21}
																	onChange={(e) => {
																		if (e.target.checked) {
																			arrayHelpers.push(String(21));
																		} else {
																			arrayHelpers.remove(21);
																		}
																	}}
																/>
															</Col>
															<Col className="mt-1 text-center">
																<span>View</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value={22}
																	onChange={(e) => {
																		if (e.target.checked) {
																			arrayHelpers.push(String(22));
																		} else {
																			arrayHelpers.remove(22);
																		}
																	}}
																/>
															</Col>
															<Col className="mt-1 text-center">
																<span>Delete</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value={23}
																	onChange={(e) => {
																		if (e.target.checked) {
																			arrayHelpers.push(String(23));
																		} else {
																			arrayHelpers.remove(23);
																		}
																	}}
																/>
															</Col>
															<Col className="mt-1 text-center">
																<span>Approve/Reject</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value="24"
																	checked={values.permissions.includes("24")}
																	onChange={(e) => {
																		if (e.target.checked) {
																			if (values.permissions.includes("25")) {
																				arrayHelpers.remove(values.permissions.indexOf("25"));
																			}
																			arrayHelpers.push("24");
																		} else {
																			arrayHelpers.remove(values.permissions.indexOf("24"));
																		}
																	}}
																/>
															</Col>
															<Col className="mt-1 text-center">
																<span>Review/Reject</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value="25"
																	checked={values.permissions.includes("25")}
																	onChange={(e) => {
																		if (e.target.checked) {
																			if (values.permissions.includes("24")) {
																				arrayHelpers.remove(values.permissions.indexOf("24"));
																			}
																			arrayHelpers.push("25");
																		} else {
																			arrayHelpers.remove(values.permissions.indexOf("25"));
																		}
																	}}
																/>
															</Col>
														</Row>
														{/* Budget Permissions */}
														<Row className="mt-2">
															<Col md={2} className="mt-2">
																<p className="fw-bold">Budget</p>
															</Col>
															{getPermissionsByCategory("budget").map(
																(permission) => (
																	<Col
																		md={2}
																		sm={3}
																		xs={3}
																		className="mt-1 text-center"
																		key={permission.id}
																	>
																		<span>
																			{permission.permission_name.split("-")[0]}
																		</span>
																		<Form.Check
																			type="checkbox"
																			name="permissions"
																			value={String(permission.id)}
																			checked={values.permissions.includes(
																				String(permission.id),
																			)}
																			onChange={(e) => {
																				if (e.target.checked) {
																					arrayHelpers.push(
																						String(permission.id),
																					);
																				} else {
																					const idx =
																						values.permissions.indexOf(
																							String(permission.id),
																						);
																					arrayHelpers.remove(idx);
																				}
																			}}
																		/>
																	</Col>
																),
															)}
														</Row>
														{/* Scheduling Permissions */}
														<Row className="mt-2">
															<Col md={2} className="mt-2">
																<p className="fw-bold">Scheduling</p>
															</Col>
															{getPermissionsByCategory("schedule").map(
																(permission) => (
																	<Col
																		md={2}
																		sm={3}
																		xs={3}
																		className="mt-1 text-center"
																		key={permission.id}
																	>
																		<span>
																			{permission.permission_name.split("-")[0]}
																		</span>
																		<Form.Check
																			type="checkbox"
																			name="permissions"
																			value={String(permission.id)}
																			checked={values.permissions.includes(
																				String(permission.id),
																			)}
																			onChange={(e) => {
																				if (e.target.checked) {
																					arrayHelpers.push(
																						String(permission.id),
																					);
																				} else {
																					const idx =
																						values.permissions.indexOf(
																							String(permission.id),
																						);
																					arrayHelpers.remove(idx);
																				}
																			}}
																		/>
																	</Col>
																),
															)}
														</Row>
														{/* Report Permissions */}
														<Row className="mt-2">
															<Col md={2} className="mt-2">
																<p className="fw-bold">Report</p>
															</Col>
															{getPermissionsByCategory("report").map(
																(permission) => (
																	<Col
																		md={2}
																		sm={3}
																		xs={3}
																		className="mt-1 text-center"
																		key={permission.id}
																	>
																		<span>
																			{permission.permission_name.split("-")[0]}
																		</span>
																		<Form.Check
																			type="checkbox"
																			name="permissions"
																			value={String(permission.id)}
																			checked={values.permissions.includes(
																				String(permission.id),
																			)}
																			onChange={(e) => {
																				if (e.target.checked) {
																					arrayHelpers.push(
																						String(permission.id),
																					);
																				} else {
																					const idx =
																						values.permissions.indexOf(
																							String(permission.id),
																						);
																					arrayHelpers.remove(idx);
																				}
																			}}
																		/>
																	</Col>
																),
															)}
														</Row>
														{/* Inventory Permissions */}
														<Row className="mt-2">
															<Col md={2} className="mt-2">
																<p className="fw-bold">Inventory</p>
															</Col>
															{getPermissionsByCategory("inventory").map(
																(permission) => (
																	<Col
																		md={2}
																		sm={3}
																		xs={3}
																		className="mt-1 text-center"
																		key={permission.id}
																	>
																		<span>
																			{permission.permission_name.split("-")[0]}
																		</span>
																		<Form.Check
																			type="checkbox"
																			name="permissions"
																			value={String(permission.id)}
																			checked={values.permissions.includes(
																				String(permission.id),
																			)}
																			onChange={(e) => {
																				if (e.target.checked) {
																					arrayHelpers.push(
																						String(permission.id),
																					);
																				} else {
																					const idx =
																						values.permissions.indexOf(
																							String(permission.id),
																						);
																					arrayHelpers.remove(idx);
																				}
																			}}
																		/>
																	</Col>
																),
															)}
														</Row>
														{/* Accounts Permissions */}
														<Row className="mt-2">
															<Col md={2} className="mt-2">
																<p className="fw-bold">Accounts</p>
															</Col>
															{getPermissionsByCategory("account").map(
																(permission) => (
																	<Col
																		md={2}
																		sm={3}
																		xs={3}
																		className="mt-1 text-center"
																		key={permission.id}
																	>
																		<span>
																			{permission.permission_name.split("-")[0]}
																		</span>
																		<Form.Check
																			type="checkbox"
																			name="permissions"
																			value={String(permission.id)}
																			checked={values.permissions.includes(
																				String(permission.id),
																			)}
																			onChange={(e) => {
																				if (e.target.checked) {
																					arrayHelpers.push(
																						String(permission.id),
																					);
																				} else {
																					const idx =
																						values.permissions.indexOf(
																							String(permission.id),
																						);
																					arrayHelpers.remove(idx);
																				}
																			}}
																		/>
																	</Col>
																),
															)}
														</Row>
													</div>
												)}
											/>
										</Col>
									</Row>
									<Modal.Footer className="mt-5">
										<Row>
											<Col md={6}>
												<div className="mt-1">
													<Button onClick={closeModal} className="cancel-btn">
														<span className="text-center">Cancel</span>
													</Button>
												</div>
											</Col>
											<Col md={6}>
												<div className="mt-1">
													<Button
														type="submit"
														className="border-0 create-btn bg-color-1"
													>
														{isSubmitting ? (
															<Spinner animation="border" size="sm" />
														) : (
															<span className="text-center">Create role</span>
														)}
													</Button>
												</div>
											</Col>
										</Row>
									</Modal.Footer>
								</Modal.Body>
							</Form>
						)}
					</Formik>
				</div>
			</Modal>
		</Container>
	);
};

export default CreateRole;
